import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, CalendarBlank, Storefront, ArrowRight, SoccerBall, Lightning, Trophy, Handshake } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { MATCHES_2026 } from '../data/matches2026';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// (Msg 723) Tournament windows — drive Stage 1 (countdown), Stage 2 (live),
// Stage 3 (post-tournament) of the ticker.
const TOURNAMENT_START = new Date('2026-06-11T00:00:00');  // first match (MEX vs RSA, Mexico City)
const TOURNAMENT_END   = new Date('2026-07-20T00:00:00');  // day after final

// Parse a match's date+estTime ('2026-06-12','15:00') into an absolute Date
// (UTC). June/July 2026 is EDT, so ET = UTC-4.
const matchDate = (m) => {
  // (Msg 789) Prefer kickoff_utc when available (knockout fixtures include it)
  // — otherwise fall back to the group-stage estTime+ET→UTC math.
  if (m.kickoff_utc) return new Date(m.kickoff_utc);
  const [hh, mm] = (m.estTime || '00:00').split(':').map(Number);
  const [yy, mo, dd] = m.date.split('-').map(Number);
  return new Date(Date.UTC(yy, mo - 1, dd, hh + 4, mm)); // ET → UTC
};

// (Msg 795) Return the calendar date (YYYY-MM-DD) of a Date instance in
// America/New_York. Critical because raw UTC comparisons bleed matches that
// kick off late-ET (e.g. 9pm ET → next UTC day) into the wrong calendar day.
const etDateStr = (d) => {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/New_York',
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(d);
  } catch (_e) {
    // Fallback: manual EDT (UTC-4) — accurate for Jun/Jul 2026.
    const t = new Date(d.getTime() - 4 * 60 * 60 * 1000);
    const yy = t.getUTCFullYear();
    const mo = String(t.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(t.getUTCDate()).padStart(2, '0');
    return `${yy}-${mo}-${dd}`;
  }
};

// (Msg 795) Return the ET calendar date of a match. For group-stage fixtures
// we already store the ET date literally in m.date. For knockouts (kickoff_utc
// present) we compute the ET calendar day from the UTC timestamp so
// late-night matches don't leak into the next day.
const matchEtDate = (m) => {
  if (m.kickoff_utc) return etDateStr(new Date(m.kickoff_utc));
  return m.date || '';
};

// Returns { now: match | null, next: match | null, todayCount } based on `n`.
const tournamentNowNext = (n, matchList) => {
  const nMs = n.getTime();
  let now = null;
  let next = null;
  const list = matchList && matchList.length ? matchList : MATCHES_2026;
  for (const m of list) {
    const start = matchDate(m).getTime();
    const end = start + 110 * 60 * 1000; // 90 min + extra time + half-time
    if (start <= nMs && nMs <= end) { now = m; }
    if (start > nMs && !next) { next = m; }
  }
  const todayEt = etDateStr(n);
  const todayCount = list.filter(m => matchEtDate(m) === todayEt).length;
  return { now, next, todayCount };
};

const teamFlags = (teams = '') => {
  const flagMap = {
    'Mexico': '🇲🇽', 'South Africa': '🇿🇦', 'Canada': '🇨🇦', 'Bosnia and Herzegovina': '🇧🇦',
    'USA': '🇺🇸', 'Paraguay': '🇵🇾', 'Qatar': '🇶🇦', 'Switzerland': '🇨🇭', 'Brazil': '🇧🇷',
    'Morocco': '🇲🇦', 'Haiti': '🇭🇹', 'Scotland': '🏴󠁧󠁢󠁳󠁣󠁴󠁿', 'Australia': '🇦🇺', 'Turkey': '🇹🇷',
    'Germany': '🇩🇪', 'Netherlands': '🇳🇱', 'Japan': '🇯🇵', 'Ivory Coast': '🇨🇮', 'Ecuador': '🇪🇨',
    'Sweden': '🇸🇪', 'Tunisia': '🇹🇳', 'Spain': '🇪🇸', 'Belgium': '🇧🇪', 'Egypt': '🇪🇬',
    'Saudi Arabia': '🇸🇦', 'Uruguay': '🇺🇾', 'Iran': '🇮🇷', 'New Zealand': '🇳🇿', 'France': '🇫🇷',
    'Senegal': '🇸🇳', 'Iraq': '🇮🇶', 'Norway': '🇳🇴', 'Argentina': '🇦🇷', 'Algeria': '🇩🇿',
    'Austria': '🇦🇹', 'Jordan': '🇯🇴', 'Portugal': '🇵🇹', 'DR Congo': '🇨🇩', 'England': '🏴󠁧󠁢󠁥󠁮󠁧󠁿', 'Croatia': '🇭🇷',
    'South Korea': '🇰🇷', 'Czech Republic': '🇨🇿', 'Cape Verde': '🇨🇻', 'Curaçao': '🇨🇼',
  };
  return teams.split(' vs ').map(t => `${flagMap[t.trim()] || ''} ${t.trim()}`).join(' vs ');
};

const fmtCountdown = (ms) => {
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m`;
};

export const NotificationBar = ({ onVisibilityChange }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [stats, setStats] = useState({ events_listed: null, site_fans: null });
  const [scrolled, setScrolled] = useState(false);
  // (Msg 780) Full fixture list fetched from backend — populated by the mount
  // effect below. Ref (not state) so tournamentNowNext can read it without
  // triggering re-renders every 1s.
  const allMatchesRef = useRef([]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // (Msg 723) Determine the current ticker STAGE so the marquee swaps copy:
  //   1 = pre-kickoff (countdown)
  //   2 = tournament live (day counter + now/next match)
  //   3 = post-tournament (gratitude message)
  const getStageState = () => {
    const now = new Date();
    if (now < TOURNAMENT_START) {
      const diff = TOURNAMENT_START - now;
      return {
        stage: 1,
        countdown: {
          days: Math.floor(diff / 86400000),
          hours: Math.floor((diff % 86400000) / 3600000),
          minutes: Math.floor((diff % 3600000) / 60000),
          seconds: Math.floor((diff % 60000) / 1000),
        },
      };
    }
    if (now >= TOURNAMENT_END) {
      return { stage: 3 };
    }
    // Stage 2 — tournament live
    const dayNum = Math.floor((now - TOURNAMENT_START) / 86400000) + 1;
    const totalDays = Math.ceil((TOURNAMENT_END - TOURNAMENT_START) / 86400000);
    const { now: liveMatch, next: nextMatch, todayCount } = tournamentNowNext(now, allMatchesRef.current);
    const nextCountdown = nextMatch ? matchDate(nextMatch).getTime() - now.getTime() : null;
    return {
      stage: 2, dayNum, totalDays, todayCount,
      liveMatch, nextMatch, nextCountdown,
    };
  };

  const [stageState, setStageState] = useState(getStageState);

  // (Msg 780) Fetch the full fixture list from backend on mount so the marquee
  // counts include all knockout matches (R32, R16, QF, SF, Final) — not just
  // the 72 group-stage matches statically imported from matches2026.js.
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/fifa-fixtures/public`).then(r => {
      if (!mounted) return;
      const list = (r.data && r.data.matches) ? r.data.matches : [];
      // Normalise to marquee shape { date, estTime, teams }
      const shaped = list.map(m => ({
        id: m.id,
        date: m.date,
        estTime: (m.kickoff_et_24h || '').slice(0, 5) || (m.kickoff_utc ? new Date(m.kickoff_utc).toISOString().slice(11, 16) : '00:00'),
        kickoff_utc: m.kickoff_utc,
        teams: `${m.home} vs ${m.away}`,
      }));
      allMatchesRef.current = shaped;
      setStageState(getStageState());
    }).catch(() => {});
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setStageState(getStageState()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchStats = () =>
      axios.get(`${API}/stats/headline`).then(r => { if (mounted) setStats(r.data); }).catch(() => {});
    fetchStats();
    const t = setInterval(fetchStats, 60000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    if (onVisibilityChange) onVisibilityChange(isVisible);
  }, [isVisible, onVisibilityChange]);

  if (!isVisible) return null;

  const fmt = (n) => (n == null ? '—' : n.toLocaleString());

  // A single block of content; we render it twice in the marquee track so the
  // scroll loops seamlessly from right to left.
  const MarqueeBlock = ({ ariaHidden }) => (
    <div className="flex items-center gap-6 px-4" aria-hidden={ariaHidden ? 'true' : 'false'}>
      {stageState.stage === 1 && (
        <span className="flex items-center gap-1.5 whitespace-nowrap" data-testid={ariaHidden ? undefined : 'marquee-countdown'}>
          <CalendarBlank size={15} weight="bold" />
          <span className="text-[12px] sm:text-sm font-bold uppercase tracking-tight">
            {stageState.countdown.days}d {stageState.countdown.hours}h {stageState.countdown.minutes}m {stageState.countdown.seconds}s to kickoff
          </span>
        </span>
      )}
      {stageState.stage === 2 && (
        <>
          <span className="flex items-center gap-1.5 whitespace-nowrap" data-testid={ariaHidden ? undefined : 'marquee-tournament-day'}>
            <span className="w-2 h-2 rounded-full bg-red-300 animate-pulse" />
            <span className="text-[12px] sm:text-sm font-black uppercase tracking-widest">LIVE · Day {stageState.dayNum} of {stageState.totalDays}</span>
          </span>
          <span className="opacity-50">•</span>
          {/* (Msg 792) Show BOTH the today-count chip AND the Partner CTA
              always — user wants the Partner CTA hard-coded/live regardless
              of matches today. */}
          {stageState.todayCount > 0 && (
            <>
              <span className="flex items-center gap-1.5 whitespace-nowrap" data-testid={ariaHidden ? undefined : 'marquee-today-count'}>
                <SoccerBall size={15} weight="bold" />
                <span className="text-[12px] sm:text-sm font-bold uppercase tracking-tight">{stageState.todayCount} match{stageState.todayCount === 1 ? '' : 'es'} today</span>
              </span>
              <span className="opacity-50">•</span>
            </>
          )}
          <a href="/partnerships" className="flex items-center gap-1.5 whitespace-nowrap hover:text-yellow-200 transition-colors" data-testid={ariaHidden ? undefined : 'marquee-partner-cta'} {...(ariaHidden ? { tabIndex: -1, 'aria-hidden': 'true' } : {})}>
            <Handshake size={15} weight="bold" />
            <span className="text-[12px] sm:text-sm font-black uppercase tracking-tight">Partner with World Soccer Party</span>
          </a>
          {stageState.liveMatch && (
            <>
              <span className="opacity-50">•</span>
              <span className="flex items-center gap-1.5 whitespace-nowrap" data-testid={ariaHidden ? undefined : 'marquee-live-match'}>
                <Lightning size={15} weight="fill" className="text-yellow-200" />
                <span className="text-[12px] sm:text-sm font-bold uppercase tracking-tight">NOW · {teamFlags(stageState.liveMatch.teams)}</span>
              </span>
            </>
          )}
          {stageState.nextMatch && stageState.nextCountdown != null && (
            <>
              <span className="opacity-50">•</span>
              <span className="flex items-center gap-1.5 whitespace-nowrap" data-testid={ariaHidden ? undefined : 'marquee-next-match'}>
                <CalendarBlank size={15} weight="bold" />
                <span className="text-[12px] sm:text-sm font-bold uppercase tracking-tight">
                  NEXT · {teamFlags(stageState.nextMatch.teams)} · in {fmtCountdown(stageState.nextCountdown)}
                </span>
              </span>
            </>
          )}
        </>
      )}
      {stageState.stage === 3 && (
        <span className="flex items-center gap-1.5 whitespace-nowrap" data-testid={ariaHidden ? undefined : 'marquee-postgame'}>
          <SoccerBall size={15} weight="fill" />
          <span className="text-[12px] sm:text-sm font-bold uppercase tracking-tight">🏆 The tournament's a wrap — thanks for the buzz · See the stories →</span>
        </span>
      )}
      <span className="opacity-50">•</span>
      {/* (Msg 746) Game Highlights — yellow attention pill in the ticker so
          fans see the latest recap the moment a match ends. */}
      <Link
        to="/blog/game-highlights"
        className="flex items-center gap-1.5 whitespace-nowrap bg-[#FFD700] text-[#0B1220] px-2.5 py-1 rounded-full text-[11px] sm:text-[12px] font-black uppercase tracking-wider hover:bg-yellow-300 transition-colors shadow"
        data-testid={ariaHidden ? undefined : 'marquee-game-highlights'}
      >
        <Trophy size={14} weight="fill" /> Game Highlights <ArrowRight size={12} weight="bold" />
      </Link>
      <span className="opacity-50">•</span>
      <span className="flex items-center gap-1.5 whitespace-nowrap" data-testid={ariaHidden ? undefined : 'marquee-events'}>
        <Storefront size={15} weight="bold" />
        <span className="text-[12px] sm:text-sm font-bold uppercase tracking-tight">{fmt(stats.events_listed)} live events · new listings added weekly</span>
      </span>
      <span className="opacity-50">•</span>
      <a
        href={stageState.stage === 3 ? '/blog' : '/venue-registration'}
        className="flex items-center gap-1 whitespace-nowrap text-[12px] sm:text-sm font-black uppercase tracking-wider underline-offset-2 hover:underline"
        data-testid={ariaHidden ? undefined : 'marquee-cta'}
      >
        {stageState.stage === 3 ? 'Read the recaps' : 'List Event Free'} <ArrowRight size={14} weight="bold" />
      </a>
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes wsp-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .wsp-marquee-track {
          display: inline-flex;
          align-items: center;
          animation: wsp-marquee 32s linear infinite;
          will-change: transform;
        }
        .wsp-marquee-track:hover { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .wsp-marquee-track { animation: none; }
        }
      `}</style>
      <div
        className="fixed left-0 right-0 z-[40] bg-gradient-to-r from-[#FF6B35] to-[#FF006E] text-white transition-[top] duration-300"
        style={{
          top: scrolled
            ? 'var(--wsp-header-h-scrolled, 56px)'
            : 'var(--wsp-header-h, 80px)',
        }}
        data-testid="notification-bar"
      >
        <div className="relative flex items-center">
          {/* Scrolling marquee — takes all available horizontal space */}
          <div className="flex-1 overflow-hidden py-2">
            <div className="wsp-marquee-track">
              {/* Duplicate the block so the loop is seamless */}
              <MarqueeBlock />
              <MarqueeBlock ariaHidden />
            </div>
          </div>

          {/* Fixed close button on the right (does not scroll with the marquee) */}
          <button
            onClick={() => setIsVisible(false)}
            className="text-white hover:text-gray-200 transition-colors flex-shrink-0 p-2 pr-3 sm:pr-4"
            aria-label="Close notification"
            data-testid="notification-close"
          >
            <X size={16} weight="bold" />
          </button>

          {/* Right-edge fade so content gently disappears under the close button */}
          <div className="pointer-events-none absolute top-0 right-9 sm:right-11 bottom-0 w-8 bg-gradient-to-l from-[#FF006E] to-transparent" />
        </div>
      </div>
    </>
  );
};
